import type { FC } from "react";
import { useEffect, useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { colorsPalettes } from "@similarweb/styles";
import { Button } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import type { LobItem } from "app/components/compare/WebsiteQueryBar/useLobSites";
import { translate } from "app/filters/i18nFilter";
import { AssetsService } from "app/services/AssetsService";
import { allTrackers } from "app/services/track/track";
import { isCompareMode } from "app/UtilitiesAndConstants/UtilityFunctions/isCompareMode";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import DomainCompareSelector from "../DomainCompareSelector/DomainCompareSelector";

import {
    ButtonsContainer,
    FooterContainer,
    Hr,
    LayoutContainer,
    StyledEmptyState,
    StyledWebsiteChipItem,
} from "./styledComponent";

const MAX_SUGGESTED_SITES = 5;

interface ICtaButtonsProps {
    sites: LobItem[];
}

interface ILobEmptyStateProps {
    topAnalyzedLOBWebsites: LobItem[];
    excludedSites: string[];
    compareModeWebsites: LobItem[];
}

const getFavicon = (icon: string): string => AssetsService.getFavIconPath(icon);

const FoldersButton: FC = () => {
    const { params } = useCurrentStateAndParams();
    const handleSeeFoldersClick = () => {
        allTrackers.dispatchTrackEvent("Button", "click", `empty state suggestion/Folders`);
        proNavigator.go("digitalsuite_website_folders", { ...params, selectedTab: "Folders" });
    };
    const isCompare = isCompareMode();
    const buttonText = isCompare
        ? translate("analysis.content.lob.no.data.see.folders")
        : translate("analysis.content.lob.no.data.see.folders.single.mode");
    return (
        <Button
            type="outlined"
            onClick={handleSeeFoldersClick}
            key="see-folders-btn"
        >
            {buttonText}
        </Button>
    );
};

const SingleCtaButtons: FC<ICtaButtonsProps> = ({ sites }) => {
    const handleSiteClick = (name: string) => {
        allTrackers.dispatchTrackEvent("Button", "click", `empty state suggestion/${name}`);
        const currentState = proNavigator.current();
        const params = { key: name, ...(currentState.reset ?? {}) };
        proNavigator.updateParams(params);
    };

    const suggestedSites = sites.slice(0, MAX_SUGGESTED_SITES);
    return (
        <LayoutContainer>
            <FoldersButton />
            <Hr />
            <FooterContainer>
                <Text
                    fontVariant="caption"
                    color={colorsPalettes.carbon[300]}
                >
                    {translate("analysis.content.lob.no.data.footer")}
                </Text>
                <ButtonsContainer>
                    {suggestedSites.map((item) => (
                        <div
                            key={item.name}
                            onClick={() => handleSiteClick(item.name)}
                        >
                            <StyledWebsiteChipItem
                                hideActionButton
                                text={item.name}
                                image={getFavicon(item.icon)}
                            />
                        </div>
                    ))}
                </ButtonsContainer>
            </FooterContainer>
        </LayoutContainer>
    );
};

const CompareCtaButtons: FC<ICtaButtonsProps> = ({ sites }) => {
    const domainsArr = sites
        .slice(0, MAX_SUGGESTED_SITES)
        .map((site) => site.name)
        .join(",");

    const handleCompareClick = () => {
        allTrackers.dispatchTrackEvent("Button", "click", `empty state suggestion/comparison`);
    };

    const [primaryDomain] = sites;

    if (!primaryDomain) {
        return null;
    }

    return (
        <LayoutContainer>
            <FoldersButton />
            <Hr />
            <FooterContainer>
                <Text
                    fontVariant="caption"
                    color={colorsPalettes.carbon[300]}
                >
                    {translate("analysis.content.lob.no.data.footer.compare")}
                </Text>
                <DomainCompareSelector
                    sites={sites}
                    domainsArr={domainsArr}
                    onClick={handleCompareClick}
                />
            </FooterContainer>
        </LayoutContainer>
    );
};

const LobEmptyState: FC<ILobEmptyStateProps> = ({ topAnalyzedLOBWebsites, excludedSites, compareModeWebsites }) => {
    const isCompare = isCompareMode();

    useEffect(() => {
        allTrackers.dispatchTrackEvent("empty state", "page loaded", `LOB empty state/${excludedSites.join(", ")}`);
    }, [excludedSites]);

    const { subtitle, sites } = useMemo(() => {
        const subtitle = translate(
            isCompare ? "analysis.content.lob.no.data.subtitle.compare" : "analysis.content.lob.no.data.subtitle",
        );

        const sites = isCompare && compareModeWebsites.length > 0 ? compareModeWebsites : topAnalyzedLOBWebsites;

        return { subtitle, sites };
    }, [isCompare, compareModeWebsites, topAnalyzedLOBWebsites]);

    if (!sites.length) {
        return null;
    }

    const CtaButtonsComponent = isCompare
        ? () => <CompareCtaButtons sites={sites} />
        : () => <SingleCtaButtons sites={sites} />;

    return (
        <StyledEmptyState
            title={translate("analysis.content.lob.no.data.title")}
            subtitle={subtitle}
            CtaButtons={CtaButtonsComponent}
        />
    );
};

export default LobEmptyState;
